import React, { useContext, useEffect, useState } from 'react';
import { useIonToast } from '@ionic/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DirectoryProps } from './DirectoryItem';
import { ConfirmToast } from '../globals/ConfirmToast';
import { AppContext } from '../../state/AppContextProvider';
import {
  RelationAction,
  RelationStatus,
  WP_UserNetwork,
} from '../../model/models';
import { useLogger } from '../../services/LogService';
import { useHistory } from 'react-router';
import { PhoneEntry } from './entry/PhoneEntry';
import { EmailEntry } from './entry/EmailEntry';

type ChangeStatusAction = (
  userNetwork: WP_UserNetwork | undefined,
  friendId?: number
) => Promise<WP_UserNetwork>;

const getSuccessMsgId = (action: RelationAction) => {
  return `Friend Request Action Success #${action}`;
};

const ACTION_BUTTONS: { [x in RelationAction]: any } = {
  delete: 'bg-red-500 text-white',
  request: 'bg-primary text-white',
  accept: 'bg-primary text-white',
  refuse: 'bg-red-500 text-white',
  prompt: 'bg-red-500 text-white',
  none: 'bg-red-500 text-white',
};

const getActionData = (action: RelationAction | undefined) => {
  if (action) {
    return {
      classNames: ACTION_BUTTONS[action],
      label: `Friend Request Action Label #${action}`,
    };
  } else {
    return {
      classNames: '',
      label: '',
    };
  }
};

export const DirectoryModalContent: React.FC<DirectoryProps> = ({
  userId,
  name,
  avatar,
  house,
  phone,
  email,
  friendStatus,
  network,
  create,
  update,
  remove,
  refreshUser,
}) => {
  const { state } = useContext(AppContext);

  const logger = useLogger('DirectoryModalContent');

  const [showToastBlock, setShowToastBlock] = useState<boolean>(false);
  const [showToastUnblock, setShowToastUnblock] = useState<boolean>(false);

  const intl = useIntl();
  const [present] = useIonToast();

  const history = useHistory();

  const [actionDisabled, setActionDisabled] = useState(false);
  const [action, setAction] = useState<RelationAction | undefined>(undefined);

  useEffect(() => {
    const a = getAction(friendStatus, network);
    setAction(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAction: (
    friendStatus: RelationStatus,
    network: WP_UserNetwork | undefined
  ) => RelationAction = (friendStatus, userNetwork) => {
    logger.info(`getNextStatus() current status: ${friendStatus}`);
    const me = state.user?.id;
    if (
      (friendStatus === undefined || userNetwork === undefined) &&
      userId !== me
    ) {
      return 'request';
    } else if (userNetwork !== undefined && friendStatus === 'accepted') {
      // if I received the request
      if (userNetwork.acf.user_b === me) {
        logger.info(`getNextStatus() current status: ${friendStatus}`);
        return 'refuse';
      } else {
        // if I am the sender
        return 'delete';
      }
      // network status === requested
    } else if (userNetwork !== undefined && friendStatus === 'requested') {
      // if I received the request
      if (userNetwork.acf.user_b === me) {
        return 'prompt';
      } else {
        // if I am the sender
        return 'delete';
      }
      // network status === refused
    } else if (userNetwork !== undefined && friendStatus === 'refused') {
      // if I received the request
      if (userNetwork.acf.user_b === me) {
        return 'accept';
      } else {
        // if I am the sender
        return 'delete';
      }
    } else {
      // should never go here
      logger.info('should go here when this directory user is myself');
      return 'none';
    }
  };

  const changeFriendStatus: (
    action: RelationAction,
    userNetwork: WP_UserNetwork | undefined,
    userId: number
  ) => Promise<WP_UserNetwork | undefined> = (action, userNetwork) => {
    switch (action) {
      case 'request':
        return sendFriendRequest(userNetwork, userId);
      case 'delete':
        return deleteReq(userNetwork);
      case 'accept':
        return acceptReq(network);
      case 'refuse':
        return refuseReq(network);
      case 'prompt':
      case 'none':
        return redirectToAcceptPage(network);
    }
  };

  const success = (msgId: string) => {
    present({
      color: 'primary',
      position: 'top',
      message: intl.formatMessage({ id: msgId }),
      duration: 3000,
    });
  };

  const failed = () => {
    present({
      color: 'dark',
      position: 'top',
      message: intl.formatMessage({ id: 'Friend Request Action Failed' }),
      duration: 3000,
    });
  };

  const changeStatusHandler = () => {
    if (action === undefined) {
      return;
    }
    setActionDisabled(true);
    logger.info(`changeStatusHandler() action: ${action}`);
    changeFriendStatus(action, network, userId)
      .then(
        (network) => {
          if (action !== 'prompt') {
            // display success toast
            const successMsgId = getSuccessMsgId(action);
            success(successMsgId);
          }
          if (network) {
            refreshUser(network.acf.user_b);
          }
        },
        () => {
          failed();
        }
      )
      .finally(() => setActionDisabled(false));
  };

  const sendFriendRequest: ChangeStatusAction = (userNetwork, friendId) => {
    logger.log(
      `sendFriendRequest from ${state.user?.id} to ${friendId}`,
      userNetwork
    );
    if (
      userNetwork === undefined ||
      userNetwork.acf.user_a !== state.user?.id
    ) {
      // no request or request but initiated by my friend
      return create({
        status: 'publish',
        acf: {
          user_a: (state?.user?.id || 0) as number,
          user_b: friendId as number,
          relation_status: 'requested',
        },
      });
    } else {
      return update(userNetwork?.id || 0, {
        acf: {
          user_a: (state?.user?.id || 0) as number,
          user_b: friendId as number,
          relation_status: 'requested',
        },
      });
    }
  };

  const deleteReq: ChangeStatusAction = (userNetwork) => {
    logger.log('deleteReq');
    return new Promise<WP_UserNetwork>((resolve, reject) => {
      remove(userNetwork?.id || 0).then((u) => {
        resolve(userNetwork as WP_UserNetwork);
      });
    });
  };

  const refuseReq: ChangeStatusAction = (userNetwork) => {
    logger.log('refusedFriendship');
    return update(userNetwork?.id || 0, {
      acf: {
        relation_status: 'refused',
      },
    });
  };

  const acceptReq: ChangeStatusAction = (userNetwork) => {
    logger.log('acceptReq');
    return update(userNetwork?.id || 0, {
      acf: {
        relation_status: 'accepted',
      },
    });
  };

  const redirectToAcceptPage: ChangeStatusAction = (userNetwork) => {
    logger.log('redirectToAcceptPage');
    history.push('/friend-request');
    return new Promise((resolve, reject) => {
      resolve(userNetwork as WP_UserNetwork);
    });
  };

  const actionData = getActionData(action);

  return (
    <div className="m-5 md:bg-white md:rounded-3xl md:p-5 md:m-0">
      <div className="w-16 h-0.5 bg-gray-500 m-auto mb-3 rounded-full md:hidden"></div>
      <div className="pb-7 flex justify-between border-b border-gray-700 md:border-gray-200 md:pb-4">
        <div className="flex flex-col">
          <div className="flex relative w-20">
            <img
              className="h-20 w-full rounded-full object-cover"
              src={avatar}
              alt=""
            />
          </div>
          <p className="text-xl font-medium text-white md:text-black mt-3">
            {name}
          </p>
          <p className="text-xs text-white md:text-black truncate">{house}</p>
        </div>
        {action !== 'none' && (
          <div className="flex flex-wrap">
            <span
              className={`cursor-pointer h-8 flex items-center py-4 px-5 rounded-2xl ${actionData.classNames}`}
              onClick={() => !actionDisabled && changeStatusHandler()}
            >
              {actionData.label && <FormattedMessage id={actionData.label} />}
            </span>
          </div>
        )}
      </div>
      <PhoneEntry phone={phone} />
      <EmailEntry email={email} />
      <ConfirmToast
        showToast={showToastBlock}
        hideToast={setShowToastBlock}
        onAccept={() => {
          if (network !== null) {
            refuseReq(network);
          }
        }}
        onCancel={() => {}}
        acceptText="Block"
        refusedText="Cancel"
        messageText="preventionBlock"
      />
      <ConfirmToast
        showToast={showToastUnblock}
        hideToast={setShowToastUnblock}
        onAccept={() => {
          if (network !== null) {
            refuseReq(network);
          }
        }}
        onCancel={() => {}}
        acceptText="Unblock"
        refusedText="Cancel"
        messageText="preventionUnblock"
      />
    </div>
  );
};
